import React, { useState, useMemo } from 'react';
import {
    Box, Typography, Paper, Card, CardContent, Chip, Avatar,
    Stack, Grid, IconButton, Tooltip, Button, Select, MenuItem,
    FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
    DialogActions, List, ListItem, ListItemText, ListItemAvatar
} from '@mui/material';
import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Flight as FlightIcon,
    Person as PersonIcon,
    Today as TodayIcon,
    CalendarMonth as CalendarIcon
} from '@mui/icons-material';

interface TravelForm {
    id: number;
    staffName: string;
    email: string;
    projectNumber: string;
    projectName: string;
    client: string;
    destination: string[];
    travelDateFrom: string;
    travelDateTo: string;
    status: string;
    department: string;
    submittedDate?: string | null;
    lastModified?: string;
    // Allow additional properties for flexibility
    [key: string]: any;
}

interface TravelCalendarProps {
    travelForms: TravelForm[];
    onFormSelect?: (form: TravelForm) => void;
    viewMode?: 'month' | 'week';
}

const TravelCalendar: React.FC<TravelCalendarProps> = ({
    travelForms,
    onFormSelect,
    viewMode = 'month'
}) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedForm, setSelectedForm] = useState<TravelForm | null>(null);
    const [showDetailsDialog, setShowDetailsDialog] = useState(false);

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const getDaysInMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const getMonthDays = useMemo(() => {
        const daysInMonth = getDaysInMonth(currentDate);
        const firstDay = getFirstDayOfMonth(currentDate);
        const days = [];

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < firstDay; i++) {
            days.push(null);
        }

        // Add days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(day);
        }

        return days;
    }, [currentDate]);

    const getTravelForDay = (day: number | null) => {
        if (!day) return [];

        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();
        const targetDate = new Date(currentYear, currentMonth, day);

        return travelForms.filter(form => {
            if (form.status !== 'approved') return false;

            const fromDate = new Date(form.travelDateFrom);
            const toDate = new Date(form.travelDateTo);

            return targetDate >= fromDate && targetDate <= toDate;
        });
    };

    const getInitials = (name: string) => {
        return name.split(' ').map(n => n[0]).join('').toUpperCase();
    };

    const getDepartmentColor = (department: string) => {
        const colors: { [key: string]: string } = {
            'Technology': '#1976d2',
            'Consulting': '#388e3c',
            'Operations': '#f57c00',
            'Design': '#7b1fa2',
            'Marketing': '#d32f2f'
        };
        return colors[department] || '#757575';
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            if (direction === 'prev') {
                newDate.setMonth(prev.getMonth() - 1);
            } else {
                newDate.setMonth(prev.getMonth() + 1);
            }
            return newDate;
        });
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    const handleFormClick = (form: TravelForm) => {
        setSelectedForm(form);
        setShowDetailsDialog(true);
        if (onFormSelect) {
            onFormSelect(form);
        }
    };

    const isToday = (day: number | null) => {
        if (!day) return false;
        const today = new Date();
        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();

        return today.getDate() === day &&
            today.getMonth() === currentMonth &&
            today.getFullYear() === currentYear;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-GB');
    };

    const upcomingTravel = useMemo(() => {
        const today = new Date();
        const nextMonth = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000));

        return travelForms
            .filter(form => {
                const travelDate = new Date(form.travelDateFrom);
                return travelDate >= today && travelDate <= nextMonth && form.status === 'approved';
            })
            .sort((a, b) => new Date(a.travelDateFrom).getTime() - new Date(b.travelDateFrom).getTime());
    }, [travelForms]);

    return (
        <Box>
            <Grid container spacing={3}>
                {/* Calendar View */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 2 }}>
                        {/* Calendar Header */}
                        <Box sx={{ mb: 3 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Box sx={{ 
                                        bgcolor: 'primary.main', 
                                        borderRadius: 2, 
                                        p: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <CalendarIcon sx={{ color: 'white', fontSize: 28 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h5" fontWeight="bold">
                                            Travel Calendar
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            View and manage team travel schedules
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<TodayIcon />}
                                        onClick={goToToday}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        Today
                                    </Button>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.default', borderRadius: 2, p: 0.5 }}>
                                        <IconButton onClick={() => navigateMonth('prev')} size="small">
                                            <ChevronLeftIcon />
                                        </IconButton>
                                        <Typography variant="h6" sx={{ minWidth: '180px', textAlign: 'center', fontWeight: 600 }}>
                                            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                                        </Typography>
                                        <IconButton onClick={() => navigateMonth('next')} size="small">
                                            <ChevronRightIcon />
                                        </IconButton>
                                    </Box>
                                </Stack>
                            </Stack>
                            {/* Month Summary Bar */}
                            <Grid container spacing={1}>
                                <Grid item xs={4}>
                                    <Paper sx={{ p: 1, bgcolor: 'info.light', borderRadius: 2 }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <FlightIcon sx={{ color: 'info.dark' }} />
                                            <Box>
                                                <Typography variant="h6" color="info.dark">
                                                    {travelForms.filter(f => {
                                                        const date = new Date(f.travelDateFrom);
                                                        return date.getMonth() === currentDate.getMonth() &&
                                                               date.getFullYear() === currentDate.getFullYear() &&
                                                               f.status === 'approved';
                                                    }).length}
                                                </Typography>
                                                <Typography variant="caption" color="info.dark">Active Trips</Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Grid>
                                <Grid item xs={4}>
                                    <Paper sx={{ p: 1, bgcolor: 'success.light', borderRadius: 2 }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <PersonIcon sx={{ color: 'success.dark' }} />
                                            <Box>
                                                <Typography variant="h6" color="success.dark">
                                                    {new Set(travelForms.filter(f => {
                                                        const date = new Date(f.travelDateFrom);
                                                        return date.getMonth() === currentDate.getMonth() &&
                                                               date.getFullYear() === currentDate.getFullYear() &&
                                                               f.status === 'approved';
                                                    }).map(f => f.staffName)).size}
                                                </Typography>
                                                <Typography variant="caption" color="success.dark">Travellers</Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Grid>
                                <Grid item xs={4}>
                                    <Paper sx={{ p: 1, bgcolor: 'warning.light', borderRadius: 2 }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <TodayIcon sx={{ color: 'warning.dark' }} />
                                            <Box>
                                                <Typography variant="h6" color="warning.dark">
                                                    {travelForms.filter(f => {
                                                        const today = new Date();
                                                        const from = new Date(f.travelDateFrom);
                                                        const to = new Date(f.travelDateTo);
                                                        return today >= from && today <= to && f.status === 'approved';
                                                    }).length}
                                                </Typography>
                                                <Typography variant="caption" color="warning.dark">Travelling Now</Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Calendar Grid */}
                        <Box>
                            {/* Days of week header */}
                            <Grid container>
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                    <Grid item xs key={day} sx={{ textAlign: 'center', py: 1 }}>
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                            {day}
                                        </Typography>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Calendar days */}
                            <Grid container>
                                {getMonthDays.map((day, index) => {
                                    const travelOnDay = getTravelForDay(day);
                                    const isCurrentDay = isToday(day);

                                    return (
                                        <Grid item xs key={index}>
                                            <Box
                                                sx={{
                                                    minHeight: '110px',
                                                    border: '1px solid',
                                                    borderColor: isCurrentDay ? 'primary.main' : 'grey.200',
                                                    borderWidth: isCurrentDay ? 2 : 1,
                                                    bgcolor: day ? (travelOnDay.length > 0 ? 'primary.50' : 'background.paper') : 'grey.50',
                                                    position: 'relative',
                                                    p: 0.5,
                                                    borderRadius: 1,
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        bgcolor: day ? 'action.hover' : 'grey.100',
                                                        transform: day ? 'scale(1.02)' : 'none',
                                                        boxShadow: day ? 2 : 0,
                                                        zIndex: day ? 1 : 0
                                                    }
                                                }}
                                            >
                                                {day && (
                                                    <>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: isCurrentDay ? 'bold' : 'normal',
                                                                color: isCurrentDay ? 'primary.main' : 'text.primary',
                                                                bgcolor: isCurrentDay ? 'primary.light' : 'transparent',
                                                                borderRadius: isCurrentDay ? '50%' : 0,
                                                                width: isCurrentDay ? '20px' : 'auto',
                                                                height: isCurrentDay ? '20px' : 'auto',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: '0.75rem'
                                                            }}
                                                        >
                                                            {day}
                                                        </Typography>

                                                        {/* Travel indicators */}
                                                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                                            {travelOnDay.slice(0, 2).map((form, idx) => (
                                                                <Tooltip
                                                                    key={form.id}
                                                                    title={
                                                                        <Box>
                                                                            <Typography variant="caption" fontWeight="bold">{form.staffName}</Typography>
                                                                            <Typography variant="caption" display="block">{form.destination.join(', ')}</Typography>
                                                                            <Typography variant="caption" display="block" color="primary.light">
                                                                                {formatDate(form.travelDateFrom)} - {formatDate(form.travelDateTo)}
                                                                            </Typography>
                                                                        </Box>
                                                                    }
                                                                    arrow
                                                                    placement="top"
                                                                >
                                                                    <Box
                                                                        onClick={() => handleFormClick(form)}
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 0.5,
                                                                            p: 0.5,
                                                                            borderRadius: 1,
                                                                            bgcolor: 'primary.main',
                                                                            color: 'white',
                                                                            cursor: 'pointer',
                                                                            fontSize: '0.65rem',
                                                                            transition: 'all 0.2s',
                                                                            '&:hover': {
                                                                                bgcolor: 'primary.dark',
                                                                                transform: 'translateX(2px)'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <FlightIcon sx={{ fontSize: '0.7rem' }} />
                                                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                                                                            {form.staffName.split(' ')[0]}
                                                                        </Typography>
                                                                    </Box>
                                                                </Tooltip>
                                                            ))}
                                                            {travelOnDay.length > 2 && (
                                                                <Chip
                                                                    size="small"
                                                                    label={`+${travelOnDay.length - 2} more`}
                                                                    sx={{
                                                                        height: '18px',
                                                                        fontSize: '0.6rem',
                                                                        bgcolor: 'warning.light',
                                                                        color: 'warning.dark',
                                                                        fontWeight: 600
                                                                    }}
                                                                />
                                                            )}
                                                        </Stack>
                                                    </>
                                                )}
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    </Paper>
                </Grid>

                {/* Upcoming Travel Sidebar */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Upcoming Travel
                            </Typography>

                            {upcomingTravel.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No upcoming approved travel
                                </Typography>
                            ) : (
                                <List dense>
                                    {upcomingTravel.map((form) => (
                                        <ListItem
                                            key={form.id}
                                            sx={{
                                                px: 0,
                                                cursor: 'pointer',
                                                '&:hover': { bgcolor: 'grey.50' }
                                            }}
                                            onClick={() => handleFormClick(form)}
                                        >
                                            <ListItemAvatar>
                                                <Avatar
                                                    sx={{
                                                        bgcolor: getDepartmentColor(form.department),
                                                        width: 32,
                                                        height: 32,
                                                        fontSize: '0.75rem'
                                                    }}
                                                >
                                                    {getInitials(form.staffName)}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {form.staffName}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Box>
                                                        <Typography variant="caption" display="block">
                                                            {form.destination.join(', ')}
                                                        </Typography>
                                                        <Typography variant="caption" color="primary">
                                                            {formatDate(form.travelDateFrom)}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}

                            {/* Quick Stats */}
                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                    This Month
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <Paper sx={{ p: 1, bgcolor: 'primary.light', textAlign: 'center' }}>
                                            <Typography variant="h6" color="primary.dark">
                                                {travelForms.filter(f => {
                                                    const date = new Date(f.travelDateFrom);
                                                    return date.getMonth() === currentDate.getMonth() &&
                                                           date.getFullYear() === currentDate.getFullYear() &&
                                                           f.status === 'approved';
                                                }).length}
                                            </Typography>
                                            <Typography variant="caption">Trips</Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Paper sx={{ p: 1, bgcolor: 'success.light', textAlign: 'center' }}>
                                            <Typography variant="h6" color="success.dark">
                                                {new Set(travelForms.filter(f => {
                                                    const date = new Date(f.travelDateFrom);
                                                    return date.getMonth() === currentDate.getMonth() &&
                                                           date.getFullYear() === currentDate.getFullYear() &&
                                                           f.status === 'approved';
                                                }).map(f => f.staffName)).size}
                                            </Typography>
                                            <Typography variant="caption">Travellers</Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Details Dialog */}
            <Dialog
                open={showDetailsDialog}
                onClose={() => setShowDetailsDialog(false)}
                maxWidth="md"
                fullWidth
            >
                {selectedForm && (
                    <>
                        <DialogTitle>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <FlightIcon />
                                Travel Details - {selectedForm.staffName}
                            </Stack>
                        </DialogTitle>
                        <DialogContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Staff Member
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedForm.staffName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {selectedForm.department} • {selectedForm.email}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Project
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedForm.projectName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {selectedForm.projectNumber} • {selectedForm.client}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Destination
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedForm.destination.join(', ')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Travel Dates
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatDate(selectedForm.travelDateFrom)} - {formatDate(selectedForm.travelDateTo)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowDetailsDialog(false)}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default TravelCalendar;
